import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import { fmtAmount } from '../utils/numberFormat';
import { Button, Modal, FormField, ListPageHeader, FilterBar } from '../components/ui';
import { CardHero } from '../components/card';
import { TAB_ICONS } from '../utils/tabIcons';

const STATUS_LABELS = { pending: 'Beklemede', collected: 'Tahsil Edildi', cancelled: 'İptal' };
const STATUS_COLORS = { pending: '#d97706', collected: '#16a34a', cancelled: '#64748b' };

function fmt(n) { return fmtAmount(n); }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString('tr-TR') : '—'; }

function PosSlipsPage() {
  const [items, setItems] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [posList, setPosList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', status: 'pending', pos_id: '' });

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showCollectModal, setShowCollectModal] = useState(false);
  const [collectForm, setCollectForm] = useState({ date: new Date().toISOString().slice(0, 10) });
  const [collecting, setCollecting] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get('/pos-slips', { params });
      if (r.data.success) setItems(r.data.data.items);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filters, search]);

  const fetchLookups = useCallback(async () => {
    try {
      const [b, p] = await Promise.all([
        api.get('/bank-accounts'),
        api.get('/bank-pos'),
      ]);
      if (b.data.success) setBankAccounts((b.data.data.items || []).filter(x => x.is_active));
      if (p.data.success) setPosList((p.data.data.items || []).filter(x => x.is_active));
    } catch {}
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchLookups(); }, [fetchLookups]);

  const toggleSel = (id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    const pending = items.filter(i => i.status === 'pending');
    const allSelected = pending.length > 0 && pending.every(i => selectedIds.has(i.id));
    setSelectedIds(() => allSelected ? new Set() : new Set(pending.map(i => i.id)));
  };

  const selectedSlips = items.filter(i => selectedIds.has(i.id) && i.status === 'pending');
  const totalSelected = selectedSlips.reduce((s, l) => s + parseFloat(l.net_amount || 0), 0);
  const totalGross = selectedSlips.reduce((s, l) => s + parseFloat(l.amount || 0), 0);
  const totalCommission = selectedSlips.reduce((s, l) => s + parseFloat(l.commission_amount || 0) + parseFloat(l.bsmv_amount || 0), 0);

  const openCollect = () => {
    if (selectedSlips.length === 0) { setMsg({ type: 'error', text: 'Tahsil edilecek slip seçin' }); return; }
    // POS kartında banka tanımsız slip kontrolü
    const missing = selectedSlips.filter(s => !s.pos_bank_account_id);
    if (missing.length > 0) {
      setMsg({ type: 'error', text: `${missing.length} slipin POS kartında banka tanımlı değil — önce POS kartına banka bağlayın` });
      return;
    }
    setCollectForm({ date: new Date().toISOString().slice(0, 10) });
    setShowCollectModal(true); setMsg(null);
  };

  const doCollect = async () => {
    setCollecting(true);
    try {
      const res = await api.post('/pos-slips/bulk-collect', {
        slip_ids: [...selectedIds].filter(id => selectedSlips.some(s => s.id === id)),
        date: collectForm.date,
      });
      if (res.data.warning) alert(res.data.warning);
      setShowCollectModal(false);
      setSelectedIds(new Set());
      fetchList();
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.message || 'Tahsilat başarısız' }); }
    finally { setCollecting(false); }
  };

  // Seçili slipleri bankalara göre grupla (modal'da göstermek için)
  const groupedByBank = selectedSlips.reduce((acc, s) => {
    const key = s.pos_target_bank_name || '(Banka tanımlı değil)';
    if (!acc[key]) acc[key] = { count: 0, gross: 0, net: 0 };
    acc[key].count++;
    acc[key].gross += parseFloat(s.amount) || 0;
    acc[key].net   += parseFloat(s.net_amount) || 0;
    return acc;
  }, {});

  const handleDelete = async (id) => {
    if (!window.confirm('Slip silinsin mi?')) return;
    try {
      await api.delete('/pos-slips/' + id);
      fetchList();
    } catch (err) { alert(err.response?.data?.message || 'Silinemedi'); }
  };

  const handleUncollect = async (slip) => {
    if (!window.confirm(
      `Bu slip ${slip.collected_bank_name || ''} hesabına ${fmt(slip.net_amount)} TL olarak tahsil edilmiş. ` +
      `Geri alınsın mı?\n\nBu işlem:\n` +
      `• Bankadan tutarı düşer\n` +
      `• Slibi "Bekleyen" durumuna döndürür\n` +
      `• Muhasebe kaydını iptal eder`
    )) return;
    try {
      await api.post(`/pos-slips/${slip.id}/uncollect`);
      fetchList();
    } catch (err) { alert(err.response?.data?.message || 'Geri alınamadı'); }
  };

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="POS Slip Tahsilat"
        count={items.length}
        unit="slip"
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Slip no, cari, POS..."
        onRefresh={fetchList}
        refreshing={loading}
        actions={selectedSlips.length > 0 ? (
          <Button size="sm" onClick={openCollect}>
            Tahsil Et ({selectedSlips.length})
          </Button>
        ) : null}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'date_from', type: 'date', label: 'İlk Tarih' },
          { key: 'date_to', type: 'date', label: 'Son Tarih' },
          { key: 'status', type: 'select', label: 'Durum', options: [
            { value: 'pending', label: 'Bekleyenler' },
            { value: 'collected', label: 'Tahsil Edildi' },
            { value: 'cancelled', label: 'İptal' },
            { value: '', label: 'Tümü' },
          ] },
          { key: 'pos_id', type: 'select', label: 'POS', flex: '1 1 200px', minWidth: 180, options: [
            { value: '', label: "Tüm POS'lar" },
            ...posList.map(p => ({ value: p.id, label: p.pos_name })),
          ] },
        ]}
      />

      {/* Özet */}
      {filters.status === 'pending' && items.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1, padding: '10px 14px', background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 8 }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>Seçili Slipler</div>
            <div style={{ fontSize: '1rem', fontWeight: 700 }}>{selectedSlips.length}</div>
          </div>
          <div style={{ flex: 1, padding: '10px 14px', background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 8 }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>Brüt Toplam</div>
            <div style={{ fontSize: '1rem', fontWeight: 700, fontFamily: 'monospace' }}>{fmt(totalGross)}</div>
          </div>
          <div style={{ flex: 1, padding: '10px 14px', background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 8 }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>Komisyon + BSMV</div>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--danger)', fontFamily: 'monospace' }}>-{fmt(totalCommission)}</div>
          </div>
          <div style={{ flex: 1, padding: '10px 14px', background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 8 }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>Net Tahsilat</div>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--success)', fontFamily: 'monospace' }}>+{fmt(totalSelected)}</div>
          </div>
        </div>
      )}

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="pos-slips-list"
          tableClassName="stocks-table"
          loading={loading}
          emptyText="Slip bulunamadı"
          data={items}
          rowKey="id"
          rowClassName={s => selectedIds.has(s.id) ? 'pos-slip-selected' : ''}
          columns={[
            { id: 'select', header: filters.status === 'pending' ? <input type="checkbox" onChange={toggleAll} checked={items.filter(i => i.status === 'pending').length > 0 && items.filter(i => i.status === 'pending').every(i => selectedIds.has(i.id))} /> : '',
              sortable: false, hideable: false, draggable: false, filterable: false, width: 36,
              cellStyle: { textAlign: 'center' },
              cell: s => s.status === 'pending' ? <input type="checkbox" checked={selectedIds.has(s.id)} onChange={(e) => { e.stopPropagation(); toggleSel(s.id); }} /> : null },
            { id: 'tx_date', header: 'Tarih', accessor: 'tx_date', sortable: true, width: 110,
              sortFn: (a,b) => new Date(a.tx_date||0)-new Date(b.tx_date||0),
              cell: s => <span style={{ whiteSpace: 'nowrap' }}>{fmtDate(s.tx_date)}</span> },
            { id: 'pos', header: 'POS', accessor: 'pos_name', sortable: true, width: 150,
              cell: s => <><strong>{s.pos_name}</strong><div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{s.pos_bank_name}</div></> },
            { id: 'account', header: 'Cari', accessor: 'account_title', sortable: true,
              cell: s => s.account_title || '—' },
            { id: 'slip_no', header: 'Slip No', accessor: s => s.slip_no || ('#' + String(s.id || '').slice(0, 8)), sortable: true, width: 140,
              cell: s => {
                const primary = s.slip_no || ('#' + String(s.id || '').slice(0, 8));
                return (
                  <div style={{ display:'flex', flexDirection:'column', lineHeight: 1.3 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600 }}>{primary}</span>
                    {s.receipt_no && (
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>Makbuz: {s.receipt_no}</span>
                    )}
                  </div>
                );
              } },
            { id: 'installment', header: 'Taksit', accessor: 'installment_count', sortable: true, width: 80,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: s => s.installment_count === 1 ? 'Tek' : s.installment_count + 'x' },
            { id: 'amount', header: 'Tutar', accessor: s => parseFloat(s.amount)||0, sortable: true, width: 130,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontFamily: 'monospace' },
              cell: s => `${fmt(s.amount)} ${s.currency_code}` },
            { id: 'commission', header: 'Kom+BSMV', accessor: s => parseFloat(s.commission_amount||0)+parseFloat(s.bsmv_amount||0), sortable: true, width: 110,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontFamily: 'monospace', color: 'var(--danger)' },
              cell: s => `-${fmt(parseFloat(s.commission_amount || 0) + parseFloat(s.bsmv_amount || 0))}` },
            { id: 'net', header: 'Net', accessor: s => parseFloat(s.net_amount)||0, sortable: true, width: 120,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: 'var(--success)' },
              cell: s => `+${fmt(s.net_amount)}` },
            { id: 'expected', header: 'Tahmini Tahsil', accessor: 'expected_collection_date', sortable: true, width: 140,
              sortFn: (a,b) => new Date(a.expected_collection_date||0)-new Date(b.expected_collection_date||0),
              cell: s => s.expected_collection_date ? <span style={{ whiteSpace: 'nowrap', fontSize: '0.78rem' }}><div>{fmtDate(s.expected_collection_date)}</div>{s.pos_target_bank_name && <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)' }}>→ {s.pos_target_bank_name}</div>}</span> : '—' },
            { id: 'status', header: 'Durum', accessor: s => STATUS_LABELS[s.status] || s.status, sortable: true, width: 130,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: s => { const color = STATUS_COLORS[s.status]; return <><span style={{ background: color + '22', color, padding: '3px 9px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600 }}>{STATUS_LABELS[s.status]}</span>{s.status === 'collected' && s.collected_bank_name && <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)', marginTop: 2 }}>→ {s.collected_bank_name}</div>}</>; } },
            { id: 'actions', header: '', sortable: false, hideable: false, draggable: false, filterable: false, width: 90,
              cell: s => <span onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>{s.status === 'pending' && <button onClick={() => handleDelete(s.id)} title="Sil" style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer' }}>✕</button>}{s.status === 'collected' && <button onClick={() => handleUncollect(s)} title="Tahsilattan geri al" style={{ background: 'none', border: '1px solid var(--border-secondary)', color: 'var(--text-secondary)', padding: '2px 8px', borderRadius: 4, fontSize: '0.72rem', cursor: 'pointer' }}>↶ Geri Al</button>}</span> },
          ]}
        />
      </div>

      {/* Toplu Tahsilat Modal */}
      {showCollectModal && (
        <Modal
          icon={null}
          title="POS Tahsilatı"
          size="sm"
          onClose={() => setShowCollectModal(false)}
          cancelAction={{ label: 'Vazgeç', onClick: () => setShowCollectModal(false) }}
          primaryAction={{ label: '✓ Tahsil Et', onClick: doCollect, loading: collecting }}
        >
          <CardHero
            icon="💳"
            code={selectedSlips.length + ' slip'}
            title="POS Toplu Tahsilat"
            kpis={[
              { label: 'Slip Sayısı', value: selectedSlips.length, color: 'info' },
              { label: 'Net Tutar', value: '+' + fmt(totalSelected), color: 'success', sublabel: 'TRY' },
            ]}
          />
          {msg && <div style={{ padding: 10, background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', borderRadius: 6, marginBottom: 10, fontSize: '0.85rem' }}>{msg.text}</div>}

          <div style={{ padding: 12, background: 'var(--bg-secondary)', borderRadius: 8, marginBottom: 14, fontSize: '0.85rem' }}>
            <div><strong>{selectedSlips.length}</strong> slip, brüt <strong>{fmt(totalGross)}</strong> TRY</div>
            <div>Komisyon+BSMV: <span style={{ color: 'var(--danger)' }}>-{fmt(totalCommission)}</span></div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: 6 }}>Net Tahsilat: <span style={{ color: 'var(--success)' }}>+{fmt(totalSelected)} TRY</span></div>
          </div>

          {/* Hedef bankalar — her POS kartının kendi bağlı bankasına yatar */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginBottom: 6 }}>Hedef bankalar (POS kartında tanımlı)</div>
            <div style={{ border: '1px solid var(--border-secondary)', borderRadius: 8, overflow: 'hidden' }}>
              {Object.entries(groupedByBank).map(([bankName, g]) => (
                <div key={bankName} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 12px', borderTop: '1px solid var(--border-secondary)',
                  fontSize: '0.85rem',
                }}>
                  <div>
                    <strong>{bankName}</strong>
                    <span style={{ marginLeft: 8, fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{g.count} slip</span>
                  </div>
                  <div style={{ fontFamily: 'monospace', color: 'var(--success)', fontWeight: 600 }}>+{fmt(g.net)} TRY</div>
                </div>
              ))}
            </div>
          </div>

          <FormField label="Tahsil Tarihi" fullWidth>
            <input type="date" value={collectForm.date} onChange={e => setCollectForm({ ...collectForm, date: e.target.value })} />
          </FormField>
        </Modal>
      )}
    </div>
  );
}

export default PosSlipsPage;
